import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from 'react-markdown';
import TrainOfThoughtBox from "./TrainOfThoughtBox";
import { CompletedFacets } from "./CompletedFacets";

const AGENT_NAME = "o3-agent";
const BASE_URL = "/api";

function Chat() {
  const [threadId, setThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastSentTime, setLastSentTime] = useState(null); // Track when user sent message
  const chatBoxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if(!localStorage.getItem("token")) {
        navigate("/");
    }

  
    async function createThread() {
      try {
        console.log(`${BASE_URL}/agents/create-thread`)
        const response = await fetch(
          `${BASE_URL}/agents/create-thread`,
          { method: "POST" }
        );
        const data = await response.json();
        setThreadId(data.thread_id);
        console.log("Thread created:", data);
      } catch (error) {
        console.error("Failed to create thread:", error);
      }
    }
    createThread();
  }, [navigate]);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSend = async () => {
    if (!input.trim() || !threadId || loading) return;

    const userMessage = { role: "user", text: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);
    setLastSentTime(Date.now()); // Record time when user sends message
    try {
      const runResponse = await fetch(
        `${BASE_URL}/agents/${AGENT_NAME}/threads/${threadId}/messages`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: input }),
        }
      );
      const runData = await runResponse.json();
      const responseTime = Date.now() - lastSentTime; // ms
      console.log(runData, "RUNNNDDD")
      const trainOfThought = runData.train_of_thought || [];
      console.log("trainOfThought received:", trainOfThought);
      // Only add assistant messages, not user message again
      // If references exist, attach them to the last assistant message
      const assistantMessages = runData.messages.map((m, i) => {
        // Only attach references and responseTime to the last assistant message
        if (i === runData.messages.length - 1 && runData.references ) {
          return { role: "assistant", text: m, references: runData.references, responseTime , trainOfThought : trainOfThought, completedFacets: runData.completed_facets || []};
        }
        // Attach responseTime to all assistant messages
        return { role: "assistant", text: m, responseTime };
      });
      setMessages((prev) => [
        ...prev,
        ...assistantMessages,
      ]);
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // For now: simply reload page
    localStorage.removeItem("token");
    navigate("/");

  };

  function renderMessageText(text) {
    // Render markdown using ReactMarkdown
    return <ReactMarkdown>{text}</ReactMarkdown>;
  }
  console.log("Messages:", messages);
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={handleLogout} style={styles.logoutButton}>
          Logout
        </button>
        <h2>PDFLens Chat</h2>
      </div>

      <div style={styles.chatBox} ref={chatBoxRef}>
        {messages.map((msg, idx) => (
          <div
            key={idx}
            style={{
              ...styles.message,
              ...(msg.role === "user" ? styles.userMessage : styles.assistantMessage),
              alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
              position: "relative",
            }}
          >
            <strong>{msg.role === "user" ? "You" : "Agent"}:</strong>{" "}
            {renderMessageText(msg.text)}
            {/* Show response time for agent messages */}
            {/* {msg.role === "assistant" && typeof msg.responseTime === 'number' && (
              <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
                Response time: {Math.round(msg.responseTime / 1000)}s
              </div>
            )} */}
            <TrainOfThoughtBox trainOfThought={msg.trainOfThought ?? []} /> 
            <CompletedFacets facets={msg.completedFacets ?? []} />
            {/* Render citation chips if present */}
            {msg.role === "assistant" && msg.references && Array.isArray(msg.references) && msg.references.length > 0 && (
              <ReferencesGrouped references={msg.references} />
            )}
          </div>
        ))}

        {loading && (
          <div style={{ alignSelf: "flex-start", color: "#888", marginTop: 8 }}>
            <span className="loader" style={styles.loader}></span> Agent is typing...
          </div>
        )}
      </div>

      <div style={styles.inputContainer}>
        <input
          style={styles.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your message..."
          disabled={loading}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !loading) {
              handleSend();
            }
          }}
        />
        <button
          style={{
            ...styles.button,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? "not-allowed" : "pointer",
          }}
          onClick={handleSend}
          disabled={loading}
        >
          {loading ? "Sending..." : "Send"}
        </button>
      </div>

      <style>
        {`
          .loader {
            display: inline-block;
            width: 16px;
            height: 16px;
            border: 2px solid #ccc;
            border-top: 2px solid #007bff;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
            margin-right: 8px;
            vertical-align: middle;
          }
          @keyframes spin {
            0% { transform: rotate(0deg);}
            100% { transform: rotate(360deg);}
          }
        `}
      </style>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: 1100,
    width: "95%",
    margin: "30px auto",
    fontFamily: "Arial, sans-serif",
    padding: 20,
    border: "1px solid #ddd",
    borderRadius: 8,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  logoutButton: {
    padding: "6px 12px",
    background: "#dc3545",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 14,
  },
  chatBox: {
    display: "flex",
    flexDirection: "column",
    height: "70vh",
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: 10,
    marginBottom: 10,
    background: "#f9f9f9",
    overflowY: "auto",
  },
  message: {
    maxWidth: "80%",
    padding: 8,
    borderRadius: 5,
    marginBottom: 6,
    whiteSpace: "pre-wrap",
    background: "#f1f1f1", // default background for all messages
    color: "#222",
    border: "1px solid #e0e0e0",
  },
  // Add specific styles for user and assistant messages
  userMessage: {
    background: "#d1f5e6", // light green
    color: "#1a3c2b",
    border: "1px solid #7ed6a7",
  },
  assistantMessage: {
    background: "#e3f0fa", // light blue
    color: "#1a3556",
    border: "1px solid #7eb6e6",
  },
  inputContainer: {
    display: "flex",
    gap: 8,
  },
  input: {
    flex: 1,
    padding: 10,
    fontSize: 16,
    border: "1px solid #ccc",
    borderRadius: 4,
  },
  button: {
    padding: "10px 20px",
    background: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
  },
  loader: {
    display: "inline-block",
    width: 16,
    height: 16,
    border: "2px solid #ccc",
    borderTop: "2px solid #007bff",
    borderRadius: "50%",
    marginRight: 8,
    verticalAlign: "middle",
  },
  referencesBox: {
    width: '100%',
    background: '#f5f8fa',
    border: '1px solid #e3eaf2',
    borderRadius: 8,
    padding: '12px 16px',
    marginTop: 10,
    marginBottom: 4,
    boxSizing: 'border-box',
  },
  referenceItem: {
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '1px solid #e3eaf2',
  },
  referenceText: {
    fontSize: 14,
    color: '#222',
    marginTop: 2,
    whiteSpace: 'pre-line',
  },
  citationChipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 6,
  },
  citationChip: {
    display: "inline-block",
    background: "#c7e0fa", // soft blue for citation chip
    color: "#1a3556",
    borderRadius: 12,
    padding: "2px 10px",
    fontSize: 12,
    marginRight: 4,
    cursor: "pointer",
    border: "1px solid #7eb6e6",
    transition: "background 0.2s",
    maxWidth: 180,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    position: "relative",
    fontWeight: 500,
  },
  citationTooltip: {
    position: "absolute",
    left: 0,
    top: "120%",
    zIndex: 10,
    background: "#f3f4f6", // slightly darker tooltip
    color: "#222",
    border: "1px solid #888",
    borderRadius: 6,
    padding: "10px 14px",
    fontSize: 13,
    minWidth: 220,
    maxWidth: 350,
    boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
    whiteSpace: "pre-line",
    wordBreak: "break-word",
  },
};

// Add this component at the bottom of the file (before export default Chat)
export function ReferencesGrouped({ references }) {
  // Group references by publication_name (fallback to publication_title)
  const grouped = references.reduce((acc, ref) => {
    const pub =
      ref.publication_name && ref.publication_name.trim()
        ? ref.publication_name
        : ref.publication_title || ref.title;
    if (!acc[pub]) {
      acc[pub] = {
        refs: [],
        summary: ref.summary,
        conclusion: ref.conclusion,
        author: ref.author,
        drug_topic: ref.drug_topic,
        type: ref.type,
        link: ref.drive_link || ref.url || ref.citation,
        is_pdf_available: ref.is_pdf_available,
      };
    }
    acc[pub].refs.push(ref);
    return acc;
  }, {});

  return (
    <div>
      {Object.entries(grouped).map(([pub, entity], i) => (
        <ReferenceFileGroup
          key={pub + i}
          publicationTitle={pub}
          refs={entity.refs}
          summary={entity.summary}
          conclusion={entity.conclusion}
          author={entity.author}
          drug_topic={entity.drug_topic}
          type={entity.type}
          link={entity.link}
          is_pdf_available={entity.is_pdf_available}
        />
      ))}
    </div>
  );
}

function ReferenceFileGroup({ publicationTitle, refs, summary, conclusion, author, drug_topic, type, link, is_pdf_available }) {
  const [expanded, setExpanded] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState(null);
  // Get all unique page numbers
  const pages = Array.from(new Set(refs.map(r => r.page_name).filter(Boolean)));
  // Check if any reference has is_pdf_available true
  const isAbstractOnly = refs.some(r => r.is_pdf_available);
  console.log("isAbstractOnly for", publicationTitle, ":", isAbstractOnly);
  console.log("is_pdf_available for", publicationTitle, ":", is_pdf_available);

  return (
    <div style={{
      marginBottom: 18,
      border: '2px solid #e3eaf2',
      borderRadius: 8,
      background: '#f5f8fa',
      padding: '12px 16px',
      boxSizing: 'border-box',
      boxShadow: expanded ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', gap: 16 }} onClick={() => setExpanded(e => !e)}>
        <span style={{ fontWeight: 600, color: '#1a3556', fontSize: 16, marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {publicationTitle}
          {!is_pdf_available && (
            <span style={{
              background: '#d1f5e6',
              color: '#1a3c2b',
              borderRadius: 12,
              padding: '2px 10px',
              fontSize: 13,
              fontWeight: 500,
              marginLeft: 8,
              border: '1px solid #7ed6a7',
              display: 'inline-block',
              
            }}>Abstract only</span>
          )}
          {is_pdf_available && link && (
            <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'underline', fontSize: 14, marginLeft: 10 }}>
              [Link]
            </a>
          )}
        </span>
        {is_pdf_available &&<span style={{ fontSize: 14, color: '#1a3556', marginRight: 24 }}>
          Pages: {pages.length > 0 ? pages.join(', ') : 'N/A'}
        </span>}
        <span style={{ color: '#888', fontSize: 13 }}>{expanded ? '▲' : '▼'}</span>
      </div>
      {expanded && (
        <div style={{ marginTop: 10 }}>
          {refs.map((ref, idx) => {
            // Look for imagekit URLs in the content
            let contentImages = [];
            if (typeof ref.content === 'string') {
              const imgRegex = /https:\/\/ik\.imagekit\.io\/wvbac1ksvf\/[\w\-]+\.(?:jpg|jpeg|png|gif|webp|svg)/gi;
              contentImages = ref.content.match(imgRegex) || [];
            }
            return (
              <div key={idx} style={{ marginBottom: 14, cursor: 'pointer' }}
                onClick={e => {
                  e.stopPropagation();
                  setDrawerData({
                    summary: ref.summary,
                    conclusion: ref.conclusion,
                    author: ref.author,
                    drug_topic: ref.drug_topic,
                    type: ref.type,
                    findings: ref.findings,
                    limitations: ref.limitations,
                    citation_count: ref.citation_count,
                    authors: ref.authors,
                    image_url: ref.image_url,
                    references: ref.references,
                    key_biomarkers: ref.key_biomarkers
                  });
                  setDrawerOpen(true);
                }}
              >
                <div style={{ fontWeight: 500, color: '#1a3556', fontSize: 15, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}>
                  {is_pdf_available && <div>Page: {ref.page_name || 'N/A'}</div>}
                  
                </div>
                {/* Show image_url if present */}
                {ref.image_url && (
                  <img src={ref.image_url} alt="reference" style={{ maxWidth: '100%', maxHeight: 180, marginBottom: 8, borderRadius: 6, border: '1px solid #e3eaf2' }} />
                )}
                {/* Show images found in content */}
                {contentImages.length > 0 && contentImages.map((img, i) => (
                  <img key={i} src={img} alt="content-img" style={{ maxWidth: '100%', maxHeight: 180, marginBottom: 8, borderRadius: 6, border: '1px solid #e3eaf2' }} />
                ))}
                <div style={styles.referenceText}><ReactMarkdown>{ref.content || ''}</ReactMarkdown></div>
              </div>
            );
          })}
        </div>
      )}
      {drawerOpen && (
        <ReferenceDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          data={drawerData}
        />
      )}
    </div>
  );
}

function ReferenceDrawer({ open, onClose, data }) {
  const [expandedSections, setExpandedSections] = useState({});
  if (!open || !data) return null;

  // Helper to toggle section
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Section definitions
  const sections = [
    { key: 'summary', label: 'Summary', content: <ReactMarkdown>{data.summary || 'N/A'}</ReactMarkdown> },
    { key: 'conclusion', label: 'Conclusion', content: <ReactMarkdown>{data.conclusion || 'N/A'}</ReactMarkdown> },
    { key: 'findings', label: 'Findings', content: <ReactMarkdown>{data.findings || 'N/A'}</ReactMarkdown> },
    { key: 'limitations', label: 'Limitations', content: <ReactMarkdown>{data.limitations || 'N/A'}</ReactMarkdown> },
    { key: 'citation_count', label: 'Citation Count', content: data.citation_count !== undefined ? data.citation_count : 'N/A' },
    { key: 'authors', label: 'Authors', content: Array.isArray(data.authors) ? (
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {data.authors.map((a, i) => <li key={i}><ReactMarkdown>{a}</ReactMarkdown></li>)}
      </ul>
    ) : <ReactMarkdown>{data.authors || 'N/A'}</ReactMarkdown> },
  ];

  // Add references section if present and is array
  if (Array.isArray(data.references)) {
    sections.push({
      key: 'references',
      label: 'References (from paper)',
      content: (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {data.references.map((refStr, i) => (
            <li key={i}><ReactMarkdown>{refStr}</ReactMarkdown></li>
          ))}
        </ul>
      )
    });
  }

  // Add key_biomarkers section if present and is array
  if (Array.isArray(data.key_biomarkers)) {
    sections.push({
      key: 'key_biomarkers',
      label: 'Key Biomarkers',
      content: (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {data.key_biomarkers.map((bio, i) => (
            <li key={i}><ReactMarkdown>{bio}</ReactMarkdown></li>
          ))}
        </ul>
      )
    });
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      width: 400,
      height: '100vh',
      background: '#fff',
      boxShadow: '-2px 0 16px rgba(0,0,0,0.18)',
      zIndex: 1000,
      padding: 32,
      display: 'flex',
      flexDirection: 'column',
      borderLeft: '2px solid #e3eaf2',
      transition: 'transform 0.2s',
      overflowY: 'auto',
    }}>
      <button onClick={onClose} style={{ alignSelf: 'flex-end', marginBottom: 16, background: '#e3eaf2', border: 'none', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', color: '#1a3556', fontWeight: 600 }}>Close</button>
      <div style={{ fontWeight: 600, fontSize: 18, color: '#1a3556', marginBottom: 12 }}>Reference Details</div>
      {data.image_url && (
        <img src={data.image_url} alt="reference" style={{ maxWidth: '100%', maxHeight: 220, marginBottom: 18, borderRadius: 8, border: '1px solid #e3eaf2' }} />
      )}
      {sections.map((section) => (
        <div key={section.key} style={{ marginBottom: 12 }}>
          <div
            style={{ fontWeight: 500, color: '#1a3556', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}
            onClick={() => toggleSection(section.key)}
          >
            {section.label}
            <span style={{ marginLeft: 8, color: '#888', fontSize: 13 }}>{expandedSections[section.key] ? '▲' : '▼'}</span>
          </div>
          {expandedSections[section.key] && (
            <div style={{ fontSize: 15, color: '#222', marginTop: 4 }}>{section.content}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// Add this component at the bottom of the file (before export default Chat)
function ReferenceItem({ refData }) {
  const [showText, setShowText] = useState(false);
  return (
    <div style={styles.referenceItem}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        {/* <CitationChip refData={refData} /> */}
        <span
          style={{ cursor: 'pointer', fontWeight: 500, color: '#1a3556', textDecoration: showText ? 'underline' : 'none' }}
          onClick={() => setShowText((prev) => !prev)}
        >
          [{refData.file_name?.split('/').pop() || "Citation"}]
          {refData.page_name && (
            <span style={{ color: '#1a3556', fontSize: 13, marginLeft: 4 }}>
              (Page: {refData.page_name})
            </span>
          )}
        </span>
        {(refData.citation || refData.url) && (
          <a
            href={refData.citation || refData.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1a3556', textDecoration: 'underline', fontSize: 13, fontWeight: 500, marginLeft: 8 }}
          >
            [File Link]
          </a>
        )}
      </div>
      {showText && (
        <div style={styles.referenceText}>{refData.content}</div>
      )}
    </div>
  );
}

export default Chat;

// Add a custom tooltip for citation chips
function CitationChip({ refData }) {
  const [show, setShow] = useState(false);
  const chipRef = useRef(null);
  // Position tooltip to avoid overflow
  const [tooltipStyle, setTooltipStyle] = useState({});
  useEffect(() => {
    if (show && chipRef.current) {
      const rect = chipRef.current.getBoundingClientRect();
      const rightOverflow = rect.left + 350 > window.innerWidth;
      setTooltipStyle({
        left: rightOverflow ? 'auto' : 0,
        right: rightOverflow ? 0 : 'auto',
        top: '120%'
      });
    }
  }, [show]);

  // Only show file name as chip, and file link in tooltip if available
  return (
    <span
      ref={chipRef}
      style={styles.citationChip}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      tabIndex={0}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
    >
      {refData.file_name || "Citation"}
      {refData.page_name && (
        <span style={{ color: '#1a3556', fontSize: 12, marginLeft: 4 }}>
          (p. {refData.page_name})
        </span>
      )}
      {show && (
        <span style={{ ...styles.citationTooltip, ...tooltipStyle }}>
          {refData.url ? (
            <a href={refData.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1a3556', textDecoration: 'underline' }}>
              {refData.file_name || refData.url}
            </a>
          ) : (
            <span>{refData.file_name || "No file"}</span>
          )}
          {refData.page_name && (
            <div style={{ color: '#1a3556', fontSize: 13, marginTop: 4 }}>
              Page: {refData.page_name}
            </div>
          )}
        </span>
      )}
    </span>
  );
}
